<template lang="pug">
	.audio-tracker-round-button.flex-col.center.align-center(@mousedown="onMouseDown", @mouseup="movementTotal.active = false", @click="onClick", @dblclick="onDblClick"
		:style="{ width: `${width}px` }", :class="{ 'color-specified': !!color, 'has-value': !!showValue, 'active': movementTotal.active }")
		.title.font-mini.upper(v-if="title", :style="{ '--color': color }") {{title}}
		.circle-level(ref="circleLevel", :style="{ '--color': color, '--size': `${width}px` }")
		.circle-outer(:style="{ '--size': `${width}px`}")
			.circle-inner(:style="{ '--color': color }")
			.indicator(:style="{ '--value': `${getValueInDegree(innerValue)}deg`, '--color': color, '--colorb': `${color}55` }")
				.indicator-item
				.indicator-item-2
			.value-handler(v-if="showValue" :style="{ '--size': `${width}px`, '--color': color }", :title="innerValue")
				.value {{getFormattedValue()}}{{getUnit()}}
			.value-popover(v-if="!showValue") {{hintValue}}
		.scale-indicator(v-if="showValue || forceScales", v-for="indicator in scaleIndicators", :style="{ '--size': `${width}px`, '--angle': `${indicator.angle}deg`, '--scale-color': scaleColor || color  }")
			.scale-val.flex-row.align-center(:style="{ '--scale-color': scaleColor || color }")
				.inner-val {{indicator.value}}
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import VueTypes from 'vue-types';

	export default {
		translationCategory: 'audioTracker.AudioTrackerRoundButton',
		name: 'audio-tracker-round-button',
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: VueTypes.number.def(0),
			min: VueTypes.number.def(0),
			max: VueTypes.number.def(10),
			scaleDegrees: VueTypes.array.def([-30, 180]),
			step: VueTypes.number.def(1),
			stepSensivity: VueTypes.number.def(1),
			width: VueTypes.number.def(120),
			formatter: VueTypes.any.def(null),
			indicators: VueTypes.array.def([]),
			title: VueTypes.string.def(null),
			color: VueTypes.string.def(null),
			round: VueTypes.number.def(0),
			showValue: VueTypes.bool.def(true),
			unit: VueTypes.string.def(null),
			hintValueType: VueTypes.string.def('percent'),
			forceScales: VueTypes.bool.def(false),
			scaleColor: VueTypes.string.def(null),
			default: VueTypes.any.def(null)
		},
		data() {
			return {
				madeStep: 0,
				innerValue: this.value,
				movementTotal: {
					x: 0,
					y: 0,
					moved: false,
					active: false
				},
				level: [0, 0]
			};
		},
		computed: {
			scaleDegreesMin() {
				return Math.min(210, Math.max(-90, this.scaleDegrees[0]));
			},
			scaleDegreesMax() {
				return Math.min(210, Math.max(-90, this.scaleDegrees[1]));
			},
			totalScaleDegrees() {
				return this.scaleDegreesMax - this.scaleDegreesMin;
			},
			totalScaleValue() {
				return this.max - this.min;
			},
			direction() {
				return this.movementTotal.x > this.movementTotal.y ? 'x' : 'y';
			},
			scaleIndicators() {
				var indicators = [];
				if (this.indicators.length) {
					indicators = this.indicators;
				} else {
					indicators = [this.min, this.max];
				}
				return indicators.map(f => typeof(f) === 'number' ? { value: f, text: f, angle: this.getValueInDegree(f) } : { ...f, angle: this.getValueInDegree(f.value) });
			},
			hintValue() {
				if (this.hintValueType === 'value') {
					return `${this.innerValue >= 0 ? '+' : ''}${parseFloat(this.innerValue).toFixed(this.round)}${this.unit || ''}`;
				}
				return `${(this.innerValue * 100).toFixed(0)}%`;
			}
		},
		watch: {
			value(v) {
				this.innerValue = v;
			},
			level(v) {
				this.updateLevel(v);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.roundButton = this;

			this.$emitChange = _.debounce(this.emitChange, 50, {
				maxWait: 500,
				leading: false,
				trailing: true
			});
		},
		beforeDestroy() {
			delete this.$emitChange;
		},
		methods: {
			onDblClick() {
				if (__DEBUG__)
					console.debug('Dbl-click');
				this.$emit('dbl-click');

				if (this.default !== null) {
					this.setValue(this.default);
				}
			},
			updateLevel(level) {
				level = level || this.level;

				if (!level || level[0] === level[1] || !level.length) {
					this.$refs.circleLevel.style.removeProperty('--level');
					return;
				}

				let getValue = (v) => {
					let angle = this.getValueInDegree(v) - 90;
					return angle < 0 ? (360 + angle) : angle;
				};

				let v1 = getValue(level[0]);
				let v2 = getValue(level[1]);
				
				if (v1 === v2) {
					this.$refs.circleLevel.style.removeProperty('--level');
					return;
				}

				let zones = [];

				if (v2 > v1) {
					zones.push({
						start: 0,
						end: v1,
						isActive: false
					});
					zones.push({
						start: v1,
						end: v2,
						isActive: true
					});
					zones.push({
						start: v2,
						end: 360,
						isActive: false
					});
				} else {
					zones.push({
						start: 0,
						end: v2,
						isActive: true
					});
					zones.push({
						start: v2,
						end: v1,
						isActive: false
					});
					zones.push({
						start: v1,
						end: 360,
						isActive: true
					});
				}

				this.$refs.circleLevel.style.setProperty('--level', zones.map(f => `${f.isActive ? this.color : 'transparent'} ${f.start}deg ${f.end}deg`));
			},
			bindMouseMove(active) {
				if (active) {
					$(window.document).bind('mousemove', this.onMouseMove);
					$(window.document).bind('mouseup', this.onMouseUp);
				}
				else {
					$(window.document).unbind('mousemove', this.onMouseMove);
					$(window.document).unbind('mouseup', this.onMouseUp);
					this.movementTotal.active = false;
				}
			},
			onMouseUp() {
				this.bindMouseMove(false);
			},
			getFormattedValue() {
				if (this.formatter && typeof(this.formatter) === 'function')
					return this.formatter(this.innerValue);
				return this.innerValue;
			},
			getValueInDegree(val) {
				var absVal = val - this.min;
				var degree = this.scaleDegreesMin + (absVal / this.totalScaleValue) * this.totalScaleDegrees;
				return Math.min(this.scaleDegreesMax, Math.max(this.scaleDegrees[0], degree));
			},
			setValue(val) {
				this.innerValue = Math.max(this.min, Math.min(this.max, val));
				this.innerValue = parseFloat(parseFloat(this.innerValue).toFixed(this.round));
				this.$emitChange();
			},
			setValueByAngle(angle) {
				this.setValue(this.min + parseFloat(this.totalScaleValue * (angle - this.scaleDegreesMin) / this.totalScaleDegrees));
			},
			emitChange() {
				this.$emit('change', this.innerValue);
			},
			onMouseDown(e) {
				this.movementTotal = { x: 0, y: 0, moved: false, active: true };
				this.bindMouseMove(true);
			},
			onMouseMove(jqe) {
				var e = jqe && jqe.originalEvent;
				if (e.buttons === 1 && this.movementTotal.active) {
					if (!this.direction)
						this.direction = e.movementX !== 0 ? 'x' : 'y';
					this.movementTotal.x += Math.abs(e.movementX);
					this.movementTotal.y += Math.abs(e.movementY);
					var value = this.direction === 'x' ? e.movementX : -e.movementY;
					this.madeStep += value;
					if (Math.abs(this.madeStep) >= this.stepSensivity) {
						var coef = Math.abs(parseInt(this.madeStep / this.stepSensivity));
						this.setValue(this.innerValue + coef * (this.madeStep > 0 ? this.step : (-1 * this.step)));
						this.madeStep = 0;
						this.movementTotal.moved = true;
					}
				} else {
					this.movementTotal.active = false;
				}
			},
			onClick(e) {
				if (this.movementTotal.moved) return;
				var delta = this.width / 2;

				let pos = Math.max(Math.abs(e.offsetX - delta), Math.abs(e.offsetY - delta));

				if (pos < (delta * 0.4)) return;
				var angle = this.getAngle(delta, delta, e.offsetX, e.offsetY);
				if (__DEBUG__)
					console.debug('On click', { e, x: e.offsetX, y: e.offsetY, angle });
				this.setValueByAngle(angle);
			},
			getAngle(cx, cy, ex, ey) {
				var dy = ey - cy;
				var dx = ex - cx;
				var theta = Math.atan2(dy, dx);
				theta *= 180 / Math.PI;
				if (theta < 0) theta += 360; // range [0, 360)
				theta -= 180;
				if (theta < -90) theta += 360;
				return theta;
			},
			getUnit() {
				return this.unit;
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-round-button {
		@import "main/themes/generated/variables.base.scss";

		position: relative;
		user-select: none;
		z-index: 0;

		&.has-value {
			margin-right: 30px;
		}

		.circle-outer {
			pointer-events: none;
			position: relative;
			width: var(--size);
			height: var(--size);
			background: #2f2f2f;
			background: radial-gradient(circle, rgba(97, 255, 97, 0) 0%, #4e4e4e 53%, #2f2f2f 54%, rgba(0, 0, 0, 0) 60%, rgba(96, 255, 95, 0) 69%);
		}

		.circle-inner {
			pointer-events: none;
			position: absolute;
			width: 100%;
			height: 100%;
			background: $base-accent;
			background: radial-gradient(circle, #b5b5b5 25%, #9c9c9c 29%, #4C4C4D 32%, $base-accent 37%, rgba(0,0,0,0.1) 45%, rgba(204, 204, 204, 0) 75%);
		}

		.circle-level {
			pointer-events: none;
			position: absolute;
			width: calc(var(--size) - 12%);
			height: calc(var(--size) - 12%);
			background: $base-accent;
			border-radius: 50%;
			background: conic-gradient(var(--level));
		}

		.indicator {
			pointer-events: none;
			position: absolute;
			left: 0px;
			right: 0px;
			width: 100%;
			height: 100%;
			transform: rotateZ(var(--value));
			z-index: 2;
		}

		.indicator-item {
			pointer-events: none;
			position: absolute;
			width: 6.5%;
			height: 3%;
			min-height: 2px;
			background: var(--color, #61ff61);
			left: 12%;
			top: 50%;
			transform: translateY(-50%);
		}

		.indicator-item-2 {
			pointer-events: none;
			position: absolute;
			width: 25%;
			height: 1%;
			min-height: 2px;
			background: rgba(0, 0, 0, 0.25);
			left: 25%;
			top: 50%;
			transform: translateY(-50%);
		}

		.value-popover {
			position: absolute;
			transform: translateY(-50%) translateX(-50%);
			background: #fff;
			border: 0px solid rgba(180,180,180);
			border-radius: 4px;
			box-shadow: rgba(0, 0, 0, 0.7) 0px 0px 4px -1px;
			font-size: 10px;
			padding: 2px;
			transition: 0.1s;
			color: #333;
			pointer-events: none;
			opacity: 0;
			z-index: 1000;
			left: 50%;
			top: -30%;
		}

		&:hover, &.active {
			.value-popover {
				opacity: 1;
			}
		}

		.value-handler {
			background: #404048;
			padding: 4px 12px;
			border-radius: 50px;
			text-align: right;
			color: #f27649;
			margin-right: -28px;
			font-size: calc(var(--size)/120 * 16);
			position: absolute;
			bottom: 10%;
			right: 0px;
			width: 90%;
			z-index: -1;
			height: 25%;
			font-weight: bold;

			> .value {
				top: 50%;
				position: absolute;
				right: 8%;
				transform: translateY(-50%);
				font-size: .825em;
			}
		}

		.title {
			color: $base-accent;
			margin-bottom: -4px;
		}

		.scale-indicator {
			pointer-events: none;
			position: absolute;
			top: 50%;
			width: 96%;
			text-align: left;
			transform: rotate(var(--angle));
			opacity: 0.4;
			z-index: 0;
			max-height: 1px;

			.scale-val {
				pointer-events: none;
				transform: rotate(calc(-1 * var(--angle))) translateX(-50%);
				width: 5px;
				font-size: 10px;
				color: $base-accent;
				margin-left: -5px;

				> .inner-val {
					position: absolute;
				}
			}

			&:before {
				content: '';
				width: 6%;
				border-bottom: 2px solid #f27649;
				position: absolute;
				top: 50%;
				transform: translateY(-50%);
				left: 11%;
				border-bottom: 2px solid $base-accent;
			}
		}

		&:hover {
			.scale-indicator {
				opacity: 0.8;
			}
		}

		&.color-specified {
			.scale-indicator {
				&:before {
					border-bottom: 2px solid var(--scale-color);
				}
				.scale-val {
					color: var(--scale-color);
				}
			}

			.title {
				color: var(--color);
			}

			.value-handler {
				color: var(--color);
			}

			.circle-inner {
				background: $base-accent;
				background: radial-gradient(circle, #b5b5b5 25%, #9c9c9c 29%, #4C4C4D 32%, var(--color) 37%, rgba(0,0,0,0.1) 45%, rgba(204, 204, 204, 0) 75%);
			}
		}
	}
</style>
